<template>
  <div>
    <v-toolbar flat color="white">
      <v-toolbar-title>Blog Yönetimi</v-toolbar-title>
      <v-divider class="mx-3" inset vertical></v-divider>

      <v-select
        v-model="selectedTenantId"
        :items="tenantOptions"
        item-text="tenantName"
        item-value="tenantId"
        label="Tenant Seç"
        @change="onTenantChange"
        style="min-width: 150px; margin-right: 10px; margin-top: 20px;"
      ></v-select>

      <v-spacer></v-spacer>

      <v-text-field
        v-model="search"
        append-icon="search"
        label="Başlıkta ara"
        single-line
        hide-details
        style="max-width: 250px; margin-right: 10px;"
      ></v-text-field>

      <v-btn color="primary" dark class="mb-2" @click="openDialog()" :disabled="!selectedTenantId">
        <v-icon left>add</v-icon>
        Yeni Blog
      </v-btn>
    </v-toolbar>

    <div v-if="!selectedTenantId" class="text-xs-center pa-5">
      <v-icon size="48" color="grey lighten-1">info</v-icon>
      <p class="mt-2 grey--text">Lütfen önce bir tenant seçin</p>
    </div>

    <v-data-table
      v-else
      :headers="headers"
      :items="blogs"
      :loading="loading"
      :search="search"
      :rows-per-page-items="[10, 25, 50, { text: 'Tümü', value: -1 }]"
      class="elevation-1"
    >
      <template v-slot:items="props">
        <td style="width: 110px;">
          <v-img
            v-if="props.item.imagePreview"
            :src="props.item.imagePreview"
            height="60"
            width="100"
            contain
          ></v-img>
          <span v-else class="grey--text">-</span>
        </td>
        <td>{{ props.item.title }}</td>
        <td>{{ formatDate(props.item.createdDate) }}</td>
        <td>{{ formatDate(props.item.updatedDate) }}</td>
        <td class="text-xs-center">
          <v-switch
            v-model="props.item.isActive"
            color="success"
            hide-details
            class="mt-0 inline-switch"
            @change="toggleStatus(props.item)"
          ></v-switch>
        </td>
        <td class="text-xs-center">
          <v-icon small class="mr-2" @click="editItem(props.item)">edit</v-icon>
          <v-icon small color="error" @click="confirmDelete(props.item)">delete</v-icon>
        </td>
      </template>
      <template v-slot:no-data>
        <div class="text-xs-center pa-3">Henüz blog kaydı yok</div>
      </template>
    </v-data-table>

    <!-- Create / Edit Dialog -->
    <v-dialog v-model="dialog" max-width="900px" persistent scrollable>
      <v-card>
        <v-card-title>
          <span class="headline">{{ isEdit ? 'Blog Düzenle' : 'Yeni Blog' }}</span>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text style="max-height: 70vh;">
          <v-container fluid grid-list-md>
            <v-layout wrap>
              <v-flex xs12>
                <blog-image-upload v-model="editedItem.imageUrl"></blog-image-upload>
              </v-flex>
              <v-flex xs12>
                <v-text-field
                  v-model="editedItem.title"
                  label="Başlık"
                  required
                  :rules="[v => !!v || 'Başlık zorunludur']"
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <label class="v-label theme--light" style="display:block; margin-bottom:6px;">
                  İçerik
                </label>
                <vue-editor
                  v-model="editedItem.content"
                  :editor-toolbar="editorToolbar"
                  placeholder="Blog metnini giriniz..."
                ></vue-editor>
              </v-flex>
              <v-flex xs12>
                <v-switch
                  v-model="editedItem.isActive"
                  label="Aktif"
                  color="success"
                ></v-switch>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="closeDialog" :disabled="saving">İptal</v-btn>
          <v-btn color="primary" @click="save" :loading="saving" :disabled="!canSave">
            Kaydet
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirm dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline">Bloğu Sil</v-card-title>
        <v-card-text>
          "<strong>{{ deleteTarget && deleteTarget.title }}</strong>" başlıklı bloğu silmek istediğinize emin misiniz?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="deleteDialog = false" :disabled="deleting">İptal</v-btn>
          <v-btn color="error" @click="performDelete" :loading="deleting">Sil</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" top>
      {{ snackbar.text }}
      <v-btn flat dark @click="snackbar.show = false">Kapat</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { VueEditor } from 'vue2-editor';
import BlogImageUpload from '@/components/BlogImageUpload.vue';
import Api from '@/api/baseApi';

const emptyBlog = () => ({
  id: 0,
  title: '',
  content: '',
  imageUrl: '',
  isActive: true
});

export default {
  name: 'AdminBlog',
  components: { VueEditor, BlogImageUpload },
  data() {
    return {
      selectedTenantId: null,
      tenantOptions: [
        { tenantId: 1, tenantName: 'Altinda Firsat' },
        { tenantId: 2, tenantName: 'EAltin' }
      ],
      search: '',
      loading: false,
      saving: false,
      deleting: false,
      dialog: false,
      isEdit: false,
      editedItem: emptyBlog(),
      deleteDialog: false,
      deleteTarget: null,
      headers: [
        { text: 'Görsel', value: 'imageUrl', sortable: false, align: 'left' },
        { text: 'Başlık', value: 'title', align: 'left' },
        { text: 'Oluşturulma', value: 'createdDate', align: 'left' },
        { text: 'Güncellenme', value: 'updatedDate', align: 'left' },
        { text: 'Aktif', value: 'isActive', align: 'center', sortable: false },
        { text: 'İşlem', value: 'actions', sortable: false, align: 'center' }
      ],
      editorToolbar: [
        [{ header: [false, 1, 2, 3, 4] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: '' }, { align: 'center' }, { align: 'right' }],
        ['blockquote', 'code-block'],
        ['link', 'image'],
        ['clean']
      ],
      snackbar: { show: false, text: '', color: 'success' }
    };
  },
  computed: {
    blogs() {
      return this.$store.state.blogs.all;
    },
    canSave() {
      return !!this.editedItem.title && !!this.editedItem.imageUrl;
    }
  },
  methods: {
    formatDate(d) {
      if (!d) return '';
      const date = new Date(d);
      return date.toLocaleDateString('tr-TR') + ' ' + date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    },
    notify(text, color = 'success') {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    onTenantChange() {
      if (this.selectedTenantId) {
        localStorage.setItem('selectedTenantId', this.selectedTenantId.toString());
        localStorage.removeItem('selectedMpId');
        this.loadBlogs();
      }
    },
    async loadBlogs() {
      this.loading = true;
      try {
        const data = await this.$store.dispatch('blogs/getAllBlogs');
        await this.attachPreviews(data);
      } catch (e) {
        this.notify('Bloglar yüklenemedi', 'error');
      } finally {
        this.loading = false;
      }
    },
    async attachPreviews(items) {
      const tasks = items.map(async (it) => {
        if (!it.imageUrl) {
          this.$set(it, 'imagePreview', '');
          return;
        }
        if (it.imageUrl.startsWith('http://') || it.imageUrl.startsWith('https://') || it.imageUrl.startsWith('//')) {
          this.$set(it, 'imagePreview', it.imageUrl);
          return;
        }
        try {
          const r = await Api.post('/api/upload/presigned-download-url', { fileKey: it.imageUrl });
          this.$set(it, 'imagePreview', r.data.downloadUrl);
        } catch {
          this.$set(it, 'imagePreview', '');
        }
      });
      await Promise.all(tasks);
    },
    openDialog() {
      this.isEdit = false;
      this.editedItem = emptyBlog();
      this.dialog = true;
    },
    editItem(item) {
      this.isEdit = true;
      this.editedItem = {
        id: item.id,
        title: item.title,
        content: item.content || '',
        imageUrl: item.imageUrl || '',
        isActive: item.isActive
      };
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
      this.editedItem = emptyBlog();
    },
    async save() {
      this.saving = true;
      try {
        const payload = {
          title: this.editedItem.title,
          content: this.editedItem.content,
          imageUrl: this.editedItem.imageUrl,
          isActive: this.editedItem.isActive
        };
        if (this.isEdit) {
          await this.$store.dispatch('blogs/putBlog', { id: this.editedItem.id, blog: { ...payload, id: this.editedItem.id } });
          this.notify('Blog güncellendi');
        } else {
          await this.$store.dispatch('blogs/postBlog', payload);
          this.notify('Blog oluşturuldu');
        }
        await this.loadBlogs();
        this.closeDialog();
      } catch (e) {
        this.notify('Kayıt sırasında hata oluştu', 'error');
      } finally {
        this.saving = false;
      }
    },
    async toggleStatus(item) {
      try {
        await this.$store.dispatch('blogs/updateBlogStatus', {
          id: item.id,
          isActive: item.isActive
        });
        this.notify(item.isActive ? 'Blog aktif edildi' : 'Blog pasif edildi');
      } catch (e) {
        item.isActive = !item.isActive;
        this.notify('Durum güncellenemedi', 'error');
      }
    },
    confirmDelete(item) {
      this.deleteTarget = item;
      this.deleteDialog = true;
    },
    async performDelete() {
      this.deleting = true;
      try {
        await this.$store.dispatch('blogs/deleteBlog', this.deleteTarget.id);
        this.notify('Blog silindi');
        this.deleteDialog = false;
        this.deleteTarget = null;
      } catch (e) {
        this.notify('Silme işlemi başarısız', 'error');
      } finally {
        this.deleting = false;
      }
    }
  },
  async mounted() {
    const savedTenantId = localStorage.getItem('selectedTenantId');
    if (savedTenantId && savedTenantId !== '0') {
      this.selectedTenantId = parseInt(savedTenantId);
      await this.loadBlogs();
    }
  },
  beforeDestroy() {
    localStorage.setItem('selectedTenantId', '0');
    localStorage.removeItem('selectedMpId');
  }
};
</script>

<style scoped>
.inline-switch {
  display: inline-flex;
  justify-content: center;
}
</style>
